<template>
  <q-page class="history-page">
    <div class="history-container">
      <!-- Loading state -->
      <div v-if="loading" class="history-container">
        <q-skeleton type="text" width="280px" dark style="font-size: 1.5rem;" />
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 24px;">
          <q-skeleton v-for="n in 3" :key="n" type="rect" height="80px" dark style="border-radius: 16px;" />
        </div>
        <q-skeleton type="rect" height="260px" dark style="border-radius: 16px; margin-top: 24px;" />
        <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 24px;">
          <q-skeleton v-for="n in 3" :key="n" type="rect" height="72px" dark style="border-radius: 16px;" />
        </div>
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="history-header">
          <router-link to="/evaluations" class="back-link">
            <q-icon name="arrow_back" size="16px" />
            Evaluaciones
          </router-link>
          <h1 class="history-title">Mi Historial de Evaluaciones</h1>
          <p class="history-subtitle">Tu progresion como entrenador</p>
        </div>

        <!-- Instrument filter -->
        <div class="filter-bar">
          <span class="filter-label">Instrumento:</span>
          <q-select
            v-model="selectedInstrument"
            :options="filterOptions"
            option-value="value"
            option-label="label"
            emit-value
            map-options
            outlined
            dense
            dark
            :style="{ minWidth: '200px' }"
            popup-content-class="q-dark"
          />
        </div>

        <!-- Stats row -->
        <div v-if="stats" class="stats-row">
          <div class="stat-card">
            <q-icon name="fact_check" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            <div>
              <p class="stat-value">{{ stats.total }}</p>
              <p class="stat-label">Total evaluaciones</p>
            </div>
          </div>
          <div class="stat-card">
            <q-icon name="emoji_events" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            <div>
              <p class="stat-value">{{ stats.avg }}</p>
              <p class="stat-label">Score promedio</p>
            </div>
          </div>
          <div class="stat-card">
            <q-icon name="schedule" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            <div>
              <p class="stat-value">{{ stats.daysSinceLast !== null ? stats.daysSinceLast : '-' }}</p>
              <p class="stat-label">{{ stats.daysSinceLast !== null ? 'dias desde ultima' : 'Ultima eval' }}</p>
            </div>
          </div>
        </div>

        <!-- Score trend chart -->
        <div v-if="evolutionData.length > 1" class="chart-section">
          <h2 class="section-title">Evolucion de scores</h2>
          <div class="chart-wrapper" style="height: 240px;">
            <Line :data="lineChartData" :options="lineChartOptions" />
          </div>
        </div>

        <!-- Evaluation list -->
        <div v-if="filteredCycles.length > 0" class="eval-list-section">
          <h2 class="section-title-lg">Evaluaciones</h2>
          <div class="eval-list">
            <router-link
              v-for="cycle in filteredCycles"
              :key="cycle.id"
              :to="`/evaluations/results/${cycle.id}`"
              class="eval-card"
            >
              <div class="eval-card-left">
                <p class="eval-instrument">{{ INSTRUMENT_LABELS[cycle.instrument] || cycle.instrument }}</p>
                <div class="eval-meta">
                  <span v-if="cycle.completedAt" class="eval-meta-item">
                    <q-icon name="event" size="12px" />
                    {{ formatDate(cycle.completedAt) }}
                  </span>
                  <span v-if="cycle.evaluator" class="eval-meta-item">
                    <q-icon name="person" size="12px" />
                    {{ cycle.evaluator.firstName }} {{ cycle.evaluator.lastName }}
                  </span>
                  <span class="eval-occasion">{{ cycle.occasion }}</span>
                </div>
              </div>
              <div class="eval-card-right">
                <template v-if="cycle.finalScore">
                  <span class="eval-score">{{ parseFloat(String(cycle.finalScore)).toFixed(1) }}</span>
                  <q-badge
                    :label="CNAP_MAP[getCNAP(parseFloat(String(cycle.finalScore)))].label"
                    :style="{
                      background: CNAP_MAP[getCNAP(parseFloat(String(cycle.finalScore)))].badgeBg,
                      color: CNAP_MAP[getCNAP(parseFloat(String(cycle.finalScore)))].badgeColor,
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '6px',
                    }"
                  />
                </template>
              </div>
            </router-link>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="filteredCycles.length === 0 && !loading" class="history-empty">
          <q-icon name="fact_check" size="48px" :style="{ color: 'var(--oa-text-disabled)' }" />
          <p class="empty-text">Todavia no tenes evaluaciones registradas</p>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { api } from 'boot/axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'vue-chartjs';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

// ---------- Types ----------
interface CnapConfig {
  label: string;
  badgeBg: string;
  badgeColor: string;
}

interface CycleItem {
  id: string;
  instrument: string;
  finalScore: string | number | null;
  completedAt: string | null;
  occasion: string;
  evaluator?: { firstName: string; lastName: string } | null;
}

// ---------- Constants ----------
const INSTRUMENT_LABELS: Record<string, string> = {
  live_observation: 'Observacion en Vivo',
  performance_binary: 'Desempeno Operativo',
  protocol_checklist: 'Adherencia a Protocolos',
  routine_execution: 'Ejecucion de Rutinas',
};

const CNAP_MAP: Record<string, CnapConfig> = {
  C: { label: 'Critico', badgeBg: 'rgba(239,68,68,0.15)', badgeColor: '#ef4444' },
  N: { label: 'Necesita Mejora', badgeBg: 'rgba(251,146,60,0.15)', badgeColor: '#fb923c' },
  A: { label: 'Adecuado', badgeBg: 'rgba(52,211,153,0.15)', badgeColor: '#34d399' },
  P: { label: 'Positivo', badgeBg: 'rgba(201,169,110,0.15)', badgeColor: 'var(--oa-gold-full)' },
};

function getCNAP(score: number): string {
  if (score >= 9) return 'P';
  if (score >= 7) return 'A';
  if (score >= 5) return 'N';
  return 'C';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

// ---------- State ----------
const loading = ref(true);
const allCycles = ref<CycleItem[]>([]);
const selectedInstrument = ref('');

const filterOptions = computed(() => [
  { label: 'Todos', value: '' },
  { label: 'Observacion en Vivo', value: 'live_observation' },
  { label: 'Desempeno Operativo', value: 'performance_binary' },
  { label: 'Adherencia a Protocolos', value: 'protocol_checklist' },
  { label: 'Ejecucion de Rutinas', value: 'routine_execution' },
]);

// ---------- Computed ----------
const filteredCycles = computed(() => {
  if (!selectedInstrument.value) return allCycles.value;
  return allCycles.value.filter((c) => c.instrument === selectedInstrument.value);
});

const stats = computed(() => {
  const completed = filteredCycles.value.filter((c) => c.finalScore && c.completedAt);
  if (completed.length === 0) return null;

  const scores = completed.map((c) => parseFloat(String(c.finalScore)));
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const latest = completed[0];
  const latestDate = latest?.completedAt ? new Date(latest.completedAt) : null;
  const daysSinceLast = latestDate
    ? Math.floor((Date.now() - latestDate.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return { total: completed.length, avg: avg.toFixed(1), daysSinceLast };
});

const evolutionData = computed(() => {
  return filteredCycles.value
    .filter((c) => c.finalScore && c.completedAt)
    .slice()
    .reverse()
    .map((c) => ({
      date: formatDateShort(c.completedAt!),
      score: parseFloat(String(c.finalScore)),
    }));
});

const lineChartData = computed(() => ({
  labels: evolutionData.value.map((d) => d.date),
  datasets: [
    {
      label: 'Score',
      data: evolutionData.value.map((d) => d.score),
      borderColor: 'var(--color-aged-gold)',
      backgroundColor: 'rgba(201, 169, 110, 0.1)',
      fill: true,
      tension: 0.3,
      pointRadius: 4,
      pointBackgroundColor: 'var(--color-aged-gold)',
      borderWidth: 2,
    },
  ],
}));

const lineChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'var(--color-sandy-beige)',
      titleColor: 'var(--color-deep-charcoal)',
      bodyColor: 'var(--color-charcoal-mist)',
      borderColor: 'rgba(61, 55, 50, 0.12)',
      borderWidth: 1,
    },
  },
  scales: {
    y: {
      min: 0,
      max: 10,
      ticks: {
        stepSize: 2,
        color: 'rgba(160, 152, 144, 0.4)',
        font: { size: 11 },
      },
      grid: { color: 'rgba(61, 58, 54, 0.1)' },
    },
    x: {
      ticks: {
        color: 'rgba(160, 152, 144, 0.4)',
        font: { size: 10 },
      },
      grid: { color: 'rgba(61, 58, 54, 0.05)' },
    },
  },
}));

// ---------- Data loading ----------
async function fetchHistory() {
  loading.value = true;
  try {
    const { data } = await api.get('/supervision/history/me');
    allCycles.value = data;
  } catch (err) {
    console.error(err);
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  fetchHistory();
});
</script>

<style scoped lang="scss">
.history-page {
  padding: 24px;
}

.history-container {
  max-width: 800px;
  margin: 0 auto;
}

// === Header ===
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-disabled);
  text-decoration: none;
  margin-bottom: 16px;
  transition: color 0.15s ease;

  &:hover {
    color: var(--oa-gold-full);
  }
}

.history-header {
  margin-bottom: 20px;
}

.history-title {
  font-size: var(--oa-text-xl);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.history-subtitle {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-muted);
  margin: 4px 0 0 0;
}

// === Filter ===
.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.filter-label {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-muted);
  flex-shrink: 0;
}

// === Stats ===
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 16px;
}

.stat-value {
  font-size: var(--oa-text-lg);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.stat-label {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  margin: 2px 0 0 0;
}

// === Chart ===
.chart-section {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
}

.section-title {
  font-size: var(--oa-text-md);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px 0;
}

.chart-wrapper {
  position: relative;
}

// === Eval list ===
.eval-list-section {
  margin-bottom: 24px;
}

.section-title-lg {
  font-size: var(--oa-text-lg);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 12px 0;
}

.eval-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.eval-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 16px 20px;
  text-decoration: none;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
  }
}

.eval-card-left {
  min-width: 0;
}

.eval-instrument {
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.eval-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
  flex-wrap: wrap;
}

.eval-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--oa-text-xs);
  color: var(--oa-text-disabled);
}

.eval-occasion {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-disabled);
  text-transform: capitalize;
}

.eval-card-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.eval-score {
  font-size: var(--oa-text-xl);
  font-weight: 700;
  color: var(--oa-text-primary);
}

// === Empty ===
.history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 32px;
  text-align: center;
}

.empty-text {
  color: var(--oa-text-muted);
  margin: 12px 0 0 0;
}
</style>
